import { useCallback, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { createViewBusinessPartnerDeals } from "@/modules/lasalle/partners/views/ViewBusinessPartnerDeals";
import { routerPaths } from "@/modules/lasalle/router";
import { NOOP } from "@/utils/utils";
import type { TApiDeal } from "@/apis/proda_finance";
import type { TCompositionDeps } from "@/ui/compositionRoot/types";
import type { TDeal } from "@/store";
import { NO_SEGMENTS } from "./noSegments";

function mapDeal(d: TApiDeal) {
    return {
        id: d.id,
        companyId: d.company_id,
        name: d.name,
        code: d.code,
        serviceProviderId: d.service_provider_id,
        portfolioCompanyId: d.portfolio_company_id,
        financeUserIds: d.finance_user_ids ?? [],
        dealFileType: d.deal_file_type,
        glType: d.gl_type,
        importAvailable: d.import_available,
        finalFileDestination: d.final_file_destination,
        ...NO_SEGMENTS,
        monthlyDeadlineDay: d.monthly_deadline_day,
        quarterlyDeadlineDay: d.quarterly_deadline_day,
        yearEndDeadline: d.year_end_deadline,
        startPeriod: d.start_period,
        yardiEntityId: d.yardi_entity_id,
        yardiDatabase: d.yardi_database,
        acceptsOtb: d.accepts_otb,
        otbComparisonEnabled: d.otb_comparison_enabled,
        acceptsFinJournals: d.accepts_fin_journals,
        isActive: d.is_active,
        isTest: d.is_test,
        notes: d.notes,
        tbFilteringRules: [] as Array<{
            filteringColumnHeader: string;
            filteringValue: string[];
            includeFilteredValues: boolean;
        }>,
        glFilteringRules: [] as Array<{
            filteringColumnHeader: string;
            filteringValue: string[];
            includeFilteredValues: boolean;
        }>,
        // The list endpoint returns the summary shape, so both timestamps can be
        // absent even though TApiDeal types them as present.
        activatedAt: d.activated_at ?? null,
        deactivatedAt: d.deactivated_at ?? null,
        createdAt: d.created_at,
        updatedAt: d.updated_at,
    };
}

export const wireBusinessPartnerDeals = ({ store, useAppStore, api }: TCompositionDeps) => {
    const ViewBusinessPartnerDeals = createViewBusinessPartnerDeals({
        useCases: () => {
            const navigate = useNavigate();
            const dealsState = useAppStore((state) => state.dealsListState);
            const currentUser = useAppStore((state) => state.user);
            const canCreateDeal = currentUser?.permissions.includes("deal_create") ?? false;
            const canToggleActive = currentUser?.permissions.includes("deal_edit") ?? false;
            // Opening a deal's detail/overview requires deal_overview_view; BP/BP+
            // can browse the list but not drill into a deal, so rows stay
            // non-navigable for them.
            const canViewDealDetail =
                currentUser?.permissions.includes("deal_overview_view") ?? false;

            const [lastBpId, setLastBpId] = useState<string | null>(null);
            // Track whether lastBpId is a portfolio company (true) or service provider (false)
            const isPortfolioCompanyRef = useRef(false);

            const [businessPartner, setBusinessPartner] = useState<{
                data: { id: string; name: string; code: string } | null;
                loading: boolean;
                error: string | null;
            }>({ data: null, loading: true, error: null });

            const [portfolioCompanies, setPortfolioCompanies] = useState<{
                data: Array<{
                    id: string;
                    name: string;
                    code: string;
                }> | null;
                loading: boolean;
            }>({ data: null, loading: true });

            const [filterOptions, setFilterOptions] = useState<{
                serviceProviders: Array<{ id: string; name: string }>;
                portfolioCompanies: Array<{ id: string; name: string }>;
            }>({ serviceProviders: [], portfolioCompanies: [] });

            // Track the last server-table query so a post-toggle refresh can replay it
            // silently — a single-row status change should not flash the view's
            // full-page loading state (gated on dealsState === "pending").
            const lastQueryRef = useRef<Parameters<typeof api.deals.list>[0]>({});

            const loadDeals = useCallback(
                (
                    query: Parameters<typeof api.deals.list>[0],
                    opts: { silent?: boolean } = {},
                    signal?: AbortSignal,
                ) => {
                    lastQueryRef.current = query;
                    if (!opts.silent) {
                        store.getState().setDealsLoading();
                    }
                    api.deals
                        .list(query, signal)
                        .then((response) => {
                            store.getState().setDealsList({
                                items: response.items.map(mapDeal),
                                total: response.total,
                                page: response.page,
                                pageSize: response.page_size,
                                totalUnfiltered: response.total_unfiltered ?? undefined,
                            });
                        })
                        .catch((error) => {
                            // Swallow abort errors — the superseding request owns the loading state.
                            if (error instanceof DOMException && error.name === "AbortError") {
                                return;
                            }
                            store
                                .getState()
                                .setDealsError(
                                    error instanceof Error
                                        ? error
                                        : new Error("Failed to load deals"),
                                );
                        });
                },
                [],
            );

            // loadDealsForBp builds the structural filter into the query
            const loadDealsForBp = useCallback(
                (bpId: string, isPortfolioCompany: boolean) =>
                    (query: Parameters<typeof api.deals.list>[0], signal?: AbortSignal) => {
                        const structuralFilter = isPortfolioCompany
                            ? { portfolio_company_id: bpId }
                            : { service_provider_id: bpId };
                        loadDeals({ ...query, ...structuralFilter }, {}, signal);
                    },
                [loadDeals],
            );

            const onMount = useCallback(async (bpId: string) => {
                setLastBpId(bpId);
                setBusinessPartner({ data: null, loading: true, error: null });
                let isPortfolioCompany = false;
                try {
                    try {
                        const sp = await api.serviceProviders.get(bpId);
                        setBusinessPartner({
                            data: { id: sp.id, name: sp.name, code: sp.code },
                            loading: false,
                            error: null,
                        });
                    } catch {
                        const pc = await api.portfolioCompanies.get(bpId);
                        setBusinessPartner({
                            data: { id: pc.id, name: pc.name, code: pc.code },
                            loading: false,
                            error: null,
                        });
                        isPortfolioCompany = true;
                    }
                    isPortfolioCompanyRef.current = isPortfolioCompany;
                } catch (err) {
                    setBusinessPartner({
                        data: null,
                        loading: false,
                        error:
                            err instanceof Error ? err.message : "Failed to load business partner",
                    });
                    return;
                }

                setPortfolioCompanies({ data: null, loading: true });
                api.portfolioCompanies
                    .list({ active_only: true, skip: 0, limit: 100 })
                    .then((pcResponse) => {
                        setPortfolioCompanies({
                            data: pcResponse.items.map((pc) => ({
                                id: pc.id,
                                name: pc.name,
                                code: pc.code,
                            })),
                            loading: false,
                        });
                    })
                    .catch(() => {
                        setPortfolioCompanies({ data: [], loading: false });
                    });

                api.deals
                    .filterOptions()
                    .then((opts) => {
                        setFilterOptions({
                            serviceProviders: opts.serviceProviders.map((o) => ({
                                id: o.id,
                                name: o.name,
                            })),
                            portfolioCompanies: opts.portfolioCompanies.map((o) => ({
                                id: o.id,
                                name: o.name,
                            })),
                        });
                    })
                    .catch(() => {
                        // non-fatal
                    });
            }, []);

            const onPageChange = useCallback((_page: number) => {
                // Page changes managed by useServerTable inside the view
            }, []);

            const onRefresh = useCallback(
                (bpId: string) => {
                    void onMount(bpId);
                },
                [onMount],
            );

            // Provide a stable loadDeals function that bakes in the structural BP filter.
            // This is rebuilt when lastBpId changes (i.e., on mount/navigate).
            const loadDealsWithBpFilter = useCallback(
                (query: Parameters<typeof api.deals.list>[0], signal?: AbortSignal) => {
                    if (!lastBpId) return;
                    loadDealsForBp(lastBpId, isPortfolioCompanyRef.current)(query, signal);
                },
                [lastBpId, loadDealsForBp],
            );

            return {
                onMount,
                onUnmount: NOOP,
                useDealsState: () => dealsState,
                useBusinessPartner: () => businessPartner,
                usePortfolioCompanies: () => portfolioCompanies,
                onPageChange,
                onDealClick: useCallback(
                    (deal: { id: string }, bpId: string) => {
                        navigate(routerPaths.admin.businessPartnerDealDetail(bpId, deal.id));
                    },
                    [navigate],
                ),
                onCreateDealClick: useCallback(
                    (bpId: string) => {
                        // The wizard reads the partner type from the route, so a portfolio
                        // company must use its own route or its id lands in service_provider_id.
                        navigate(
                            isPortfolioCompanyRef.current
                                ? routerPaths.admin.portfolioCompanyDealCreate(bpId)
                                : routerPaths.admin.businessPartnerDealCreate(bpId),
                        );
                    },
                    [navigate],
                ),
                onCopyDealClick: useCallback(
                    (deal: TDeal) => {
                        navigate(
                            deal.portfolioCompanyId
                                ? routerPaths.admin.portfolioCompanyDealCreate(
                                      deal.portfolioCompanyId,
                                      deal.id,
                                  )
                                : routerPaths.admin.businessPartnerDealCreate(
                                      deal.serviceProviderId ?? "",
                                      deal.id,
                                  ),
                        );
                    },
                    [navigate],
                ),
                onRefresh,
                onToggleActive: useCallback(
                    async (deal: { id: string; isActive: boolean }, _bpId: string) => {
                        await api.deals.update(deal.id, { is_active: !deal.isActive });
                        // Silent refresh: replay the last server-table query so a
                        // single-row status change does not flash the full-page spinner.
                        loadDeals(lastQueryRef.current, { silent: true });
                    },
                    [loadDeals],
                ),
                canCreateDeal,
                loadDeals: loadDealsWithBpFilter,
                useFilterOptions: () => filterOptions,
                canViewDealDetail,
                canToggleActive,
            };
        },
    });

    return { ViewBusinessPartnerDeals };
};
